import type {AxiosError, InternalAxiosRequestConfig} from 'axios'
import axios from 'axios'
import i18n from '../i18n'
import {getAccessToken, logout, refreshAccessToken, setAccessToken, type User} from './auth'
import {endpoints} from './endpoints'
import {formatCnpj, formatDocument} from './formatters'

export const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL}`,
})

let refreshPromise: Promise<string | null> | null = null

type RetryableRequestConfig = InternalAxiosRequestConfig<unknown> & { _retry?: boolean }

api.interceptors.request.use((config) => {
  const token = getAccessToken()
  // Ensure headers object exists
  config.headers = config.headers ?? {}
  // Always send the user's chosen language; default to pt-br if undefined
  config.headers['Accept-Language'] = i18n.language || 'pt-br'
  const publicLegalDocument = config.method === 'get' && (config.url === endpoints.public.termsOfService() || config.url === endpoints.public.privacyPolicy())
  if (token && !config.url?.startsWith('/v1/core/public/') && !publicLegalDocument) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as RetryableRequestConfig | undefined

    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true

      refreshPromise ??= refreshAccessToken().finally(() => { refreshPromise = null })
      let nextToken: string | null = null
      try {
        nextToken = await refreshPromise
      } catch {
        nextToken = null
      }
      if (nextToken) {
        setAccessToken(nextToken)
        original.headers.set('Accept-Language', 'pt-br')
        original.headers.set('Authorization', `Bearer ${nextToken}`)
        return api(original)
      } else {
        logout()
      }
    }

    return Promise.reject(error)
  }
)

// API models based on backend serializers
export interface Invoice {
  id: string
  company: string
  company_name: string
  company_cnpj: string
  key: string
  number: string
  series: string
  issuer_name: string
  issue_date: string
  total_value: string
  recipient_name: string
  recipient_document: string
  recipient_address_street: string
  recipient_address_number: string
  recipient_address_neighborhood: string
  recipient_address_city: string
  recipient_address_uf: string
  recipient_address_zip_code: string
  xml_file: string | null
  pdf_file: string | null
  danfe_generation_status?: 'not_generated' | 'pending' | 'processing' | 'available' | 'failed'
  status: string
  created_at: string
  updated_at: string
  issuer_document?: string
  issuer_address_street?: string
  issuer_address_number?: string
  issuer_address_complement?: string
  issuer_address_neighborhood?: string
  issuer_address_city?: string
  issuer_address_uf?: string
  issuer_address_zip_code?: string
  recipient_address_complement?: string
  items?: InvoiceItem[]
  data_warnings?: string[]
}

export interface InvoiceItem {
  id: string
  line_number: number
  product_code: string
  description: string
  quantity: string
  unit: string
  additional_information: string
  created_at: string
  updated_at: string
}

export interface NfeImportRun {
  id: string
  company_id: string
  source: string
  fiscal_environment: 'production' | 'homologation'
  start_date: string
  end_date: string
  timezone: string
  status: string
  coverage: 'unknown' | 'partial' | 'complete'
  coverage_detail: string
  external_protocol: string
  counters: Record<string, number>
  next_attempt_at: string | null
  error_code: string
  created_at: string
  updated_at: string
}

export interface Company {
  id: string
  is_active?: boolean
  name: string
  legal_name: string
  cnpj: string
  inscricao_estadual?: string | null
  address_uf?: string
  sefaz_environment?: 'production' | 'homologation'
  enable_sefaz_sync?: boolean
  certificate?: string | null
  certificate_serial?: string | null
  certificate_expires_at?: string | null
  address_street?: string
  address_number?: string
  address_complement?: string
  address_neighborhood?: string
  address_city?: string
  address_zip_code?: string
}

export interface DeliveryAddress {
  street: string
  number: string
  neighborhood: string
  city: string
  uf: string
  zip_code: string
  complement?: string
  reference?: string
}

export interface DeliveryParty {
  name: string
  document: string
  address_street: string
  address_number: string
  address_complement: string
  address_neighborhood: string
  address_city: string
  address_uf: string
  address_zip_code: string
}

export interface DeliveryRecipient extends DeliveryParty {
  phone: string
  email: string
}

export type DeliveryIssuer = DeliveryParty

export interface DeliveryItem {
  id?: string
  position: number
  product_code: string
  description: string
  quantity: string
  unit: string
  additional_information: string
  delivery_quantity: string
  delivery_unit: string
  observations: string
  delivered_quantity?: string | null
  missing_quantity?: string | null
}

export interface ProofOfDeliveryItem {
  id: string
  item_id: string
  product_code: string
  description: string
  expected_quantity: string
  delivered_quantity: string
  missing_quantity: string
  unit: string
}

export interface DeliveryPendency {
  id: string
  delivery: string
  delivery_code: string
  attempt: string
  item_id: string
  product_code: string
  description: string
  quantity: string
  unit: string
  is_open: boolean
  resolved_by_attempt?: string | null
  resolved_by_delivery?: string | null
  resolved_at?: string | null
  created_at?: string
}

export interface ProofOfDelivery {
  id: string
  delivery: string
  received_by_name: string
  received_by_document?: string
  recorded_by_name?: string | null
  status: string
  status_display?: string
  observations?: string
  signed_at?: string
  signed_at_server: string
  location: { longitude: number; latitude: number } | null
  signature_image: string | null
  photos: Array<{ id: string; url: string | null; created_at?: string; meta?: Record<string, unknown> }>
  item_results?: ProofOfDeliveryItem[]
  meta?: Record<string, unknown>
  created_at?: string
}

async function fetchCompaniesRequest(filters?: { account_id?: string; id?: string; search?: string }): Promise<Company[]> {
  const { data } = await api.get<Company[] | PaginatedResult<Company>>(endpoints.company.list(), {
    params: Object.fromEntries(Object.entries(filters ?? {}).filter(([, value]) => Boolean(value))),
  })
  return (Array.isArray(data) ? data : data.results).map(company => ({ ...company, cnpj: formatCnpj(company.cnpj) }))
}

export async function fetchCompanies(): Promise<Company[]> {
  return fetchCompaniesRequest()
}

export async function fetchCompaniesFiltered(filters: { account_id?: string; id?: string; search?: string }): Promise<Company[]> {
  return fetchCompaniesRequest(filters)
}

export async function saveCompany(payload: Partial<Company> & { certificate_password?: string; certificate_file?: File }) {
  const form = new FormData()
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    form.append(key === 'certificate_file' ? 'certificate' : key, value instanceof File ? value : String(value))
  })
  const url = payload.id ? endpoints.company.detail(payload.id) : endpoints.company.list()
  const { data } = payload.id
    ? await api.patch<Company>(url, form)
    : await api.post<Company>(url, form)
  return { ...data, cnpj: formatCnpj(data.cnpj) }
}

export async function deleteCompany(companyId: string): Promise<void> {
  await api.delete(endpoints.company.detail(companyId))
}

export async function fetchInvoices(): Promise<Invoice[]> {
  const { data } = await api.get<Invoice[] | PaginatedResult<Invoice>>(endpoints.invoice.list())
  return (Array.isArray(data) ? data : data.results).map(invoice => ({
    ...invoice,
    company_cnpj: formatCnpj(invoice.company_cnpj),
    recipient_document: formatDocument(invoice.recipient_document),
  }))
}

export async function fetchInvoicePage(pageUrl?: string, companyId?: string, filters?: { issue_date__gte?: string; issue_date__lte?: string; status?: string }): Promise<PaginatedResult<Invoice>> {
  if (pageUrl) {
    const allowedOrigin = new URL(import.meta.env.VITE_API_BASE_URL || window.location.origin, window.location.origin).origin
    if (new URL(pageUrl, allowedOrigin).origin !== allowedOrigin) throw new Error('A API retornou um endereço de paginação inválido.')
  }
  const { data } = await api.get<Invoice[] | PaginatedResult<Invoice>>(pageUrl ?? endpoints.invoice.list(), {
    params: pageUrl ? undefined : Object.fromEntries(Object.entries({ company: companyId, ...filters }).filter(([, value]) => value !== undefined && value !== '')),
  })
  if (Array.isArray(data)) return { count: data.length, next: null, previous: null, results: data.map(invoice => ({
    ...invoice,
    company_cnpj: formatCnpj(invoice.company_cnpj),
    recipient_document: formatDocument(invoice.recipient_document),
  })) }
  if (data && typeof data === 'object' && Array.isArray(data.results) && typeof data.count === 'number') {
    return { ...data, results: data.results.map(invoice => ({
      ...invoice,
      company_cnpj: formatCnpj(invoice.company_cnpj),
      recipient_document: formatDocument(invoice.recipient_document),
    })) }
  }
  throw new Error('A resposta da API de notas fiscais não corresponde ao formato esperado.')
}

export async function importInvoiceXml(companyId: string, file: File): Promise<{ invoice: Invoice; created: boolean }> {
  const form = new FormData()
  form.append('company', companyId)
  form.append('xml_file', file)
  const { data } = await api.post<{ invoice: Invoice; created: boolean }>(endpoints.invoice.importXml(), form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return { ...data, invoice: {
    ...data.invoice,
    company_cnpj: formatCnpj(data.invoice.company_cnpj),
    recipient_document: formatDocument(data.invoice.recipient_document),
  } }
}

export async function createNfeImportRun(payload: {
  company_id: string
  source: 'sefaz_rj_fisco_facil'
  fiscal_environment: 'production' | 'homologation'
  start_date: string
  end_date: string
  timezone?: string
}, idempotencyKey: string): Promise<NfeImportRun> {
  const { data } = await api.post<NfeImportRun>(endpoints.invoice.importRuns(), payload, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
  return data
}

export async function fetchNfeImportRuns(): Promise<NfeImportRun[]> {
  const { data } = await api.get<NfeImportRun[] | PaginatedResult<NfeImportRun>>(endpoints.invoice.importRuns())
  return Array.isArray(data) ? data : data.results
}

export async function fetchInvoice(invoiceId: string): Promise<Invoice> {
  const { data } = await api.get<Invoice>(endpoints.invoice.detail(invoiceId))
  return { ...data, company_cnpj: formatCnpj(data.company_cnpj), recipient_document: formatDocument(data.recipient_document) }
}

export async function saveInvoice(invoiceId: string, payload: Partial<Invoice>): Promise<Invoice> {
  const { data } = await api.patch<Invoice>(endpoints.invoice.detail(invoiceId), payload)
  return { ...data, company_cnpj: formatCnpj(data.company_cnpj), recipient_document: formatDocument(data.recipient_document) }
}

export async function generateInvoiceDanfe(invoiceId: string, options: { overwrite?: boolean } = {}): Promise<Invoice> {
  const { data } = await (options.overwrite
    ? api.post<Invoice>(endpoints.invoice.generateDanfe(invoiceId), { overwrite: true })
    : api.post<Invoice>(endpoints.invoice.generateDanfe(invoiceId)))
  return { ...data, company_cnpj: formatCnpj(data.company_cnpj), recipient_document: formatDocument(data.recipient_document) }
}

export async function fetchProofsOfDelivery(): Promise<ProofOfDelivery[]> {
  const { data } = await api.get<ProofOfDelivery[] | PaginatedResult<ProofOfDelivery>>(endpoints.delivery.pods())
  return Array.isArray(data) ? data : data.results
}

export interface Delivery {
  status_display: string
  id: string // UUID
  code: string
  status: 'pending' | 'delivered' | 'failed' | 'partial'
  priority: string
  priority_display?: string
  company?: string
  origin?: 'invoice' | 'manual'
  is_manual: boolean
  reference?: string
  invoice_key: string | null
  invoice_number: string | null
  invoice_series: string | null
  invoice_issuer_date: string | null
  invoice?: Invoice | null
  danfe?: DeliveryDanfe | null
  delivery_address: DeliveryAddress | null
  recipient: DeliveryRecipient | null
  issuer: DeliveryIssuer | null
  items: DeliveryItem[]
  observations?: string | null
  assigned_to?: number | null
  assigned_to_name?: string | null
  planned_delivery_at?: string | null
  created_at?: string
  updated_at?: string
  delivery_at?: string | null
  proofs?: ProofOfDelivery[]
  pendencies?: DeliveryPendency[]
  attempts_count?: number
  latest_attempt?: { id: string; status: string; recorded_at: string } | null
  completed_proof_id?: string | null
}

export interface DeliveryDanfe {
  id: string
  number: string
  series: string
  xml_file: string | null
  pdf_file: string | null
  danfe_generation_status?: Invoice['danfe_generation_status']
  status: string
}

export function deliveryDanfeDocument(delivery: Delivery): DeliveryDanfe | Invoice | null {
  return delivery.invoice ?? delivery.danfe ?? null
}

export function deliveryIsFiscal(delivery: Delivery): boolean {
  return delivery.origin === 'invoice' || !delivery.is_manual || Boolean(deliveryDanfeDocument(delivery))
}

export function deliveryInvoiceLabel(delivery: Delivery) {
  const number = delivery.invoice_number || delivery.invoice?.number
  const series = delivery.invoice_series || delivery.invoice?.series
  if (!number) return 'Sem nota fiscal'
  return series ? `${number}/${series}` : number
}

export function deliveryLabel(delivery: Delivery) {
  const invoiceLabel = deliveryInvoiceLabel(delivery)
  return invoiceLabel === 'Sem nota fiscal' ? `Entrega ${delivery.code}` : `Nota fiscal ${invoiceLabel}`
}

export function deliveryRecipientName(delivery: Delivery) {
  return delivery.recipient?.name || delivery.invoice?.recipient_name || 'Destinatário não informado'
}

export function deliveryRecipientDocument(delivery: Delivery) {
  return delivery.recipient?.document || delivery.invoice?.recipient_document || '—'
}

export function deliveryFullAddress(delivery: Delivery) {
  const address = delivery.delivery_address
  const recipient = delivery.recipient
  const values = address
    ? [address.street, address.number, address.complement, address.neighborhood, address.city, address.uf, address.zip_code]
    : recipient
      ? [recipient.address_street, recipient.address_number, recipient.address_complement, recipient.address_neighborhood, recipient.address_city, recipient.address_uf, recipient.address_zip_code]
      : delivery.invoice
        ? [delivery.invoice.recipient_address_street, delivery.invoice.recipient_address_number, delivery.invoice.recipient_address_complement, delivery.invoice.recipient_address_neighborhood, delivery.invoice.recipient_address_city, delivery.invoice.recipient_address_uf, delivery.invoice.recipient_address_zip_code]
        : []
  return values.filter(Boolean).join(' · ')
}

function normalizeDelivery(delivery: Delivery): Delivery {
  const legacyInvoice = delivery.invoice
  return {
    ...delivery,
    is_manual: delivery.is_manual ?? false,
    invoice_key: delivery.invoice_key ?? legacyInvoice?.key ?? null,
    invoice_number: delivery.invoice_number ?? legacyInvoice?.number ?? null,
    invoice_series: delivery.invoice_series ?? legacyInvoice?.series ?? null,
    invoice_issuer_date: delivery.invoice_issuer_date ?? legacyInvoice?.issue_date ?? null,
    recipient: delivery.recipient ?? (legacyInvoice ? {
      name: legacyInvoice.recipient_name,
      document: formatDocument(legacyInvoice.recipient_document),
      phone: '', email: '',
      address_street: legacyInvoice.recipient_address_street,
      address_number: legacyInvoice.recipient_address_number,
      address_complement: legacyInvoice.recipient_address_complement ?? '',
      address_neighborhood: legacyInvoice.recipient_address_neighborhood,
      address_city: legacyInvoice.recipient_address_city,
      address_uf: legacyInvoice.recipient_address_uf,
      address_zip_code: legacyInvoice.recipient_address_zip_code,
    } : null),
    issuer: delivery.issuer ?? null,
    items: delivery.items ?? [],
  }
}

export interface DeliveryTimelineEvent {
  id: string
  attempt?: string | null
  attempt_id?: string | null
  event: string
  actor: number | null
  actor_name: string | null
  assigned_to?: string | number | null
  assigned_to_name?: string | null
  created_at: string
  result: string
  reason: string
}

export interface OperationsDashboard {
  counts: { pending: number; in_progress: number; delivered: number; occurrences: number }
  total: number
  usage: null | { used: number; reserved: number; limit: number | null }
  next: string | null
  previous: string | null
  results: Delivery[]
}

export interface DeliverySummaryMetric {
  quantity: number
  delta: number | null
}

export interface DeliverySummaryByCompany {
  company_id: string
  company_name: string
  company_cnpj: string
  total: number
  delivered: number
  in_progress: number
  failed: number
  delivery_percentage: number
}

export interface DeliverySummary {
  invoices: DeliverySummaryMetric
  delivered: DeliverySummaryMetric
  in_progress: DeliverySummaryMetric
  failed: DeliverySummaryMetric
  by_company: DeliverySummaryByCompany[]
}

export interface AccountSettings {
  account_id: string
  contact_email: string
  phone: string
  default_company_id: string | null
  allow_out_of_location: boolean
  require_photo: boolean
  require_signature: boolean
  sefaz: {
    enabled: boolean
    environment: string | null
    certificate_expires_at: string | null
    last_sync_at: string | null
    status: string
  }
}

export interface AccountSettingsPayload {
  contact_email?: string
  phone?: string
  default_company_id?: string | null
  allow_out_of_location?: boolean
  require_photo?: boolean
  require_signature?: boolean
}

export interface SefazTestResult {
  status: string
  message: string
  checked_at: string
  certificate_expires_at: string | null
}

export interface DeliveryAssignmentResult { id: string; result: string; assigned_to?: number; assigned_to_name?: string | null }
export interface GenerateDeliveriesResult { invoice_id: string; delivery_id?: string; result: string }

interface DeliveryPayloadBase {
  origin: 'invoice' | 'manual'
  delivery_address?: DeliveryAddress | null
  assigned_to?: number | null
  planned_delivery_at?: string | null
  observations?: string
  priority?: 'normal' | 'urgent'
}

export interface CreateInvoiceDeliveryPayload extends DeliveryPayloadBase {
  origin: 'invoice'
  invoice: string
  item_overrides?: Array<Pick<DeliveryItem, 'position' | 'delivery_quantity' | 'delivery_unit' | 'observations'>>
}

export interface CreateManualDeliveryPayload extends DeliveryPayloadBase {
  origin: 'manual'
  company: string
  recipient: DeliveryRecipient
  issuer: DeliveryIssuer
  items: DeliveryItem[]
}

export type CreateDeliveryPayload = CreateInvoiceDeliveryPayload | CreateManualDeliveryPayload

export async function createDelivery(payload: CreateDeliveryPayload, idempotencyKey?: string): Promise<Delivery> {
  const config = payload.origin === 'manual'
    ? { headers: { 'Idempotency-Key': idempotencyKey } }
    : undefined
  const { data } = config
    ? await api.post<Delivery>(endpoints.delivery.list(), payload, config)
    : await api.post<Delivery>(endpoints.delivery.list(), payload)
  return normalizeDelivery(data)
}

export async function updateDelivery(id: string, payload: Partial<Pick<Delivery, 'delivery_address' | 'assigned_to' | 'planned_delivery_at' | 'observations' | 'priority' | 'reference' | 'recipient' | 'issuer' | 'items'>>): Promise<Delivery> {
  const { data } = await api.patch<Delivery>(endpoints.delivery.detail(id), payload)
  return normalizeDelivery(data)
}

export async function deleteDelivery(deliveryId: string): Promise<void> {
  await api.delete(endpoints.delivery.detail(deliveryId))
}

export async function linkInvoiceToDelivery(deliveryId: string, payload: { invoice_id: string; reviewed: true; review_note: string }): Promise<Delivery> {
  const { data } = await api.post<Delivery>(endpoints.delivery.linkInvoice(deliveryId), payload)
  return normalizeDelivery(data)
}

export async function fetchDeliveryReceipt(deliveryId: string): Promise<Blob> {
  const { data } = await api.get<Blob>(endpoints.delivery.receipt(deliveryId), { responseType: 'blob' })
  return data
}

export interface PresignUpload {
  filename: string
  url: string
  fields: Record<string, string>
}

export interface PresignResponse { uploads: PresignUpload[] }

export interface CreatePodPayload {
  delivery: string
  received_by_name: string
  received_by_document?: string
  signed_at: string // ISO date string from client
  location?: [number, number] | null // [lng, lat]
  signature_image?: string // URL (when not multipart)
  photos?: string[] // URLs (when not multipart)
  meta?: Record<string, unknown>
}

export type PaginatedResult<T> = {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

export type UserNotification = {
  code: string
  subject: string
  message: string
  notification_type: string
  notification_type_display?: string | null
  is_read: boolean
  created_at: string
  read_at: string | null
}

export type NotificationPage = PaginatedResult<UserNotification>

export interface PublicPlanFeature {
  label: string
  value: boolean | string
}

export interface PublicCatalog {
  trial: { duration_days: number; invoice_limit: number }
  additional_services: Array<{
    code: string; name: string; description: string; currency: string; quote_only: boolean
    setup_price: string | number | null; monthly_price: string | number | null
    unit_name: string; allows_quantity: boolean
  }>
  retention_offers: Array<{
    code: string; retention_months: number; price_cents: number | null; currency: string
    storage_limit_bytes: number; quote_only: boolean
  }>
}

export interface ProposalRequest {
  id: string
  proposal_type: 'custom_plan' | 'addon'
  status: 'requested' | 'reviewing' | 'proposed' | 'accepted' | 'declined' | 'cancelled'
  name: string
  email: string
  plan_code: string | null
  plan_name: string | null
  addon_code: string | null
  addon_name: string | null
  account_id: string | null
  has_active_plan: boolean | null
  delivery_volume: number | null
  retention_months: number | null
  company_count: number | null
  quantity: number
  notes: string
  currency_snapshot: string
  unit_price_snapshot: string | number | null
  monthly_price_snapshot: string | number | null
  total_setup_price_snapshot: string | number | null
  created_at: string
  updated_at: string
}

export interface PublicPlan {
  code: string
  name: string
  version: number
  price: number | string | null
  currency: string
  delivery_limit: number | null
  company_limit: number | null
  manager_limit: number | null
  courier_limit: number | null
  retention_months: number | null
  storage_limit_bytes: number | null
  sefaz_import?: boolean | null
  standard_support?: boolean | null
  priority_support?: boolean | null
  custom_sla?: boolean | null
  features: Record<string, unknown>
  quote_only?: boolean
  is_recommended?: boolean
  effective_at: string
}

export interface AccountMembership {
  id: string
  account: {
    id: string
    name: string
    status: string
    timezone: string
    is_in_trial: boolean
    trial_ends_at: string
  }
  role: 'manager' | 'courier'
  is_active: boolean
  can_manage_billing: boolean
  capabilities: { can_manage_billing: boolean; is_account_manager: boolean }
}

export interface BillingAccountOption { id: string; name: string; can_manage_billing: boolean }
export interface AccountUsageHistoryItem {
  month: string
  label: string
  deliveries_used: number
}
export interface PendingPlanChange {
  id: string
  plan_code: string
  plan_name: string
  status: string
  created_at: string
}
export interface AccountBillingSnapshot {
  account: { id: string; name: string }
  can_manage_billing: boolean
  subscription: null | {
    status: string; plan_code: string; plan_name: string; plan_version: number
    modality?: string; pending_plan_name?: string | null; recurring_amount_cents?: number; base_retention_months?: number | null
    price: number | string | null; currency: string; cycle_start: string; cycle_end: string
    trial_start: string | null; trial_end: string | null; delivery_limit: number | null
    company_limit: number | null; manager_limit: number | null; courier_limit: number | null
    retention_months: number | null; storage_limit_bytes: number | null
  }
  usage: null | {
    period_start: string; period_end: string; deliveries_used: number
    deliveries_reserved: number; storage_bytes: number
    companies_used?: number; managers_used?: number; couriers_used?: number
  }
  usage_history?: AccountUsageHistoryItem[]
  pending_plan_change?: PendingPlanChange | null
}

export async function fetchBillingAccounts(): Promise<BillingAccountOption[]> {
  const { data } = await api.get<{ accounts: BillingAccountOption[] }>(endpoints.account.plan())
  return data.accounts
}

export async function fetchAccountBilling(accountId: string): Promise<AccountBillingSnapshot> {
  const { data } = await api.get<AccountBillingSnapshot>(endpoints.account.plan(), { params: { account_id: accountId } })
  return data
}

export async function requestPlanChange(accountId: string, planCode: string) {
  const { data } = await api.post<{ order_id: string; status: string; detail: string; plan_name: string }>(endpoints.account.billingChangePlan(), {
    account_id: accountId,
    plan_code: planCode,
    idempotency_key: idempotencyKey(),
    consent: true,
  })
  return data
}

export type BillingAction = { available: boolean; enabled: boolean; reason?: string }
export interface BillingOrder {
  id: string
  plan_name: string
  amount_cents: number
  amount_paid_cents: number
  amount_refunded_cents: number
  balance_cents: number
  currency: string
  status: string
  provider: string | null
  due_date: string | null
  service_period_start: string | null
  service_period_end: string | null
  checkout_url: string | null
  allowed_actions: Record<string, BillingAction>
  created_at: string
  payment_attempts?: BillingPaymentAttempt[]
  payment_events?: BillingPaymentEvent[]
}
export interface BillingPaymentAttempt { id: string; status: string; amount_cents: number; reason: string | null; occurred_at: string | null }
export interface BillingPaymentEvent { id: string; status: string; received_at: string | null; processed_at: string | null }
export interface BillingOverview {
  account: { id: string; name: string }
  subscription: AccountBillingSnapshot['subscription'] & { provider?: string | null; collection_mode?: string; paid_through?: string | null; next_due_date?: string | null; cancel_at_period_end?: boolean } | null
  orders: BillingOrder[]
  capabilities: Record<string, boolean>
}

export async function fetchBillingOverview(accountId: string): Promise<BillingOverview> {
  const { data } = await api.get<BillingOverview>(endpoints.account.billingOverview(), { params: { account_id: accountId } })
  return data
}

export async function fetchBillingOrders(accountId: string, params: { status?: string; search?: string; period?: string; url?: string } = {}): Promise<PaginatedResult<BillingOrder>> {
  const { data } = await api.get<PaginatedResult<BillingOrder>>(params.url || endpoints.account.billingOrders(), { params: params.url ? undefined : { account_id: accountId, status: params.status || undefined, search: params.search || undefined, period: params.period || undefined } })
  return data
}

export async function fetchBillingOrder(accountId: string, orderId: string): Promise<BillingOrder> {
  const { data } = await api.get<BillingOrder>(endpoints.account.billingOrder(orderId), { params: { account_id: accountId } })
  return data
}

function idempotencyKey() { return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}` }

export async function requestBillingPayment(orderId: string): Promise<BillingOrder> {
  const { data } = await api.post<BillingOrder>(endpoints.account.billingPaymentAction(orderId), { idempotency_key: idempotencyKey() })
  return data
}

export async function refreshBillingOrder(orderId: string): Promise<BillingOrder> {
  const { data } = await api.post<BillingOrder>(endpoints.account.billingRefresh(orderId), { idempotency_key: idempotencyKey() })
  return data
}

export async function fetchBillingReceipt(orderId: string): Promise<{ url?: string; detail?: string }> {
  const { data } = await api.get<{ url?: string; detail?: string }>(endpoints.account.billingReceipt(orderId))
  return data
}

export async function requestBillingPaymentMethodSession(accountId: string) {
  const { data } = await api.post<{ url?: string; detail?: string }>(endpoints.account.billingPaymentMethodSession(), { account_id: accountId })
  return data
}

export async function cancelBillingRenewal(accountId: string, reason = 'Cancelamento solicitado pelo gestor') {
  const { data } = await api.post<{ status: string; access_until?: string }>(endpoints.account.billingCancelRenewal(), {
    account_id: accountId,
    reason,
    idempotency_key: idempotencyKey(),
  })
  return data
}

export interface AccountMember {
  id: string; user_id: string; name: string; email: string
  role: 'manager' | 'courier'; is_active: boolean; activation_pending: boolean; can_manage_billing: boolean
}

export async function fetchAccountMembers(accountId: string): Promise<AccountMember[]> {
  const { data } = await api.get<{ results: AccountMember[] }>(endpoints.account.memberships(), { params: { account_id: accountId } })
  return data.results
}

export async function createAccountMember(payload: { account_id: string; name: string; email: string; role: 'manager' | 'courier' }): Promise<AccountMember> {
  const { data } = await api.post<AccountMember>(endpoints.account.memberships(), payload)
  return data
}

export type AccountMemberUpdate = Partial<Pick<AccountMember, 'role' | 'is_active'>>

export async function updateAccountMember(memberId: string, payload: AccountMemberUpdate): Promise<AccountMember> {
  const { data } = await api.patch<AccountMember>(endpoints.account.membership(memberId), payload)
  return data
}

export async function resetAccountMemberPassword(memberId: string, newPassword: string): Promise<{ detail: string; must_change_password: boolean }> {
  const { data } = await api.post<{ detail: string; must_change_password: boolean }>(endpoints.account.membershipResetPassword(memberId), { new_password: newPassword })
  return data
}

export async function requestPasswordReset(email: string): Promise<{ detail: string }> {
  const { data } = await api.post<{ detail: string }>(endpoints.auth.passwordReset(), { email })
  return data
}

export async function confirmPasswordReset(token: string, newPassword: string): Promise<{ detail: string }> {
  const { data } = await api.post<{ detail: string }>(endpoints.auth.passwordResetConfirm(), { token, new_password: newPassword })
  return data
}

export interface MeResponse {
  user_id: string
  memberships: AccountMembership[]
}

export async function fetchMe(): Promise<MeResponse> {
  const { data } = await api.get<MeResponse>('/v1/accounts/me/')
  return data
}

export async function fetchPublicPlans(): Promise<PublicPlan[]> {
  const { data } = await api.get<PublicPlan[]>(endpoints.public.plans())
  if (!Array.isArray(data)) throw new Error('A resposta de planos não corresponde ao formato esperado.')
  return data
}

export async function fetchPublicCatalog(): Promise<PublicCatalog> {
  const { data } = await api.get<PublicCatalog>(endpoints.public.catalog())
  return data
}

export interface PublicLegalDocument {
  id: string
  version: number
  text: string
  published_at?: string
}

export interface AccountCancellation {
  id: string
  account_id: string
  subscription_id: string
  withdrawal: boolean
  reason: string
  created_at: string
  effective_at: string
  approved_at: string | null
  reviewed_by_id: number | null
  review_reason: string
  confirmed_at: string | null
  completed_at: string | null
  last_error: string
  support_ticket_id: string | null
  support_protocol: string | null
}

export interface AccountLegalSummary {
  acceptances: { terms__version: number; privacy_policy__version: number; accepted_at: string }[]
  data_deletion_at: string | null
  data_deleted_at: string | null
  cancellations: AccountCancellation[]
  change_notices: { id: string; account_id: string; title: string; message: string; created_at: string; effective_at: string }[]
  cancellation_options: {
    subscription_id: string | null
    plan_name: string | null
    can_request: boolean
    withdrawal_available: boolean
    withdrawal_deadline: string | null
    access_until: string | null
  }
}

export async function fetchAccountLegal(accountId: string): Promise<AccountLegalSummary> {
  const { data } = await api.get<AccountLegalSummary>(endpoints.account.legal(), { params: { account_id: accountId } })
  return data
}

export async function requestAccountCancellation(accountId: string, payload: { withdrawal: boolean; reason: string }): Promise<AccountCancellation> {
  const { data } = await api.post<AccountCancellation>(endpoints.account.cancellation(), { account_id: accountId, ...payload })
  return data
}

export async function fetchAccountDataExport(accountId: string): Promise<Blob> {
  const { data } = await api.get<Blob>(endpoints.account.dataExport(), { params: { account_id: accountId }, responseType: 'blob' })
  return data
}

export async function fetchPublicLegalDocument(kind: 'terms' | 'privacy'): Promise<PublicLegalDocument | null> {
  const endpoint = kind === 'terms' ? endpoints.public.termsOfService() : endpoints.public.privacyPolicy()
  const { data } = await api.get<PublicLegalDocument[] | PaginatedResult<PublicLegalDocument>>(endpoint)
  const documents = Array.isArray(data) ? data : data.results
  return [...documents].sort((left, right) => right.version - left.version)[0] || null
}

export async function createProposalRequest(payload: {
  proposal_type: 'custom_plan' | 'addon'; name: string; email: string
  plan_code?: string; addon_code?: string; has_active_plan?: boolean
  delivery_volume?: number; retention_months?: number; company_count?: number
  quantity?: number; notes?: string
}): Promise<ProposalRequest> {
  const { data } = await api.post<ProposalRequest>(endpoints.public.proposals(), payload)
  return data
}

export async function fetchAccountAddons(accountId: string): Promise<{
  additional_services: PublicCatalog['additional_services']; requests: ProposalRequest[]
}> {
  const { data } = await api.get<{
    additional_services: PublicCatalog['additional_services']; requests: ProposalRequest[]
  }>(endpoints.account.addons(), { params: { account_id: accountId } })
  return data
}

export async function requestAccountAddon(payload: {
  account_id: string; addon_code: string; quantity: number; notes?: string
}): Promise<ProposalRequest> {
  const { data } = await api.post<ProposalRequest>(endpoints.account.addons(), payload)
  return data
}

export async function createPublicContact(payload: { name: string; email: string; phone: string; subject: string; contact_type: string; message?: string; consent: boolean }) {
  const { data } = await api.post<{ status: 'received' }>(endpoints.public.contact(), payload)
  return data
}

export async function fetchDeliveries(
  pageUrl?: string,
  filters?: { search?: string; status?: string; status_group?: string; company_id?: string; period_start?: string; period_end?: string; ordering?: string },
): Promise<PaginatedResult<Delivery>> {
  if (pageUrl) {
    const allowedOrigin = new URL(import.meta.env.VITE_API_BASE_URL || window.location.origin, window.location.origin).origin
    const requestedUrl = new URL(pageUrl, allowedOrigin)
    if (requestedUrl.origin !== allowedOrigin) {
      throw new Error('A API retornou um endereço de paginação inválido.')
    }
  }
  const { data } = await api.get<Delivery[] | PaginatedResult<Delivery>>(pageUrl ?? endpoints.delivery.list(), {
    params: pageUrl ? undefined : Object.fromEntries(
      Object.entries(filters ?? {}).filter(([, value]) => Boolean(value)),
    ),
  })
  if (Array.isArray(data)) {
    return { count: data.length, next: null, previous: null, results: data.map(normalizeDelivery) }
  }
  if (data && typeof data === 'object' && Array.isArray(data.results)
      && typeof data.count === 'number') {
    return { ...data, results: data.results.map(normalizeDelivery) }
  }
  throw new Error('A resposta da API de entregas não corresponde ao formato esperado.')
}

export interface DeliveryReportFilters {
  account_id: string
  company_id?: string
  period_start?: string
  period_end?: string
  created_at__gte?: string
  created_at__lte?: string
  search?: string
  ordering?: 'created_at' | '-created_at'
  limit?: number
}

export async function fetchDeliveryReport(
  pageUrl?: string,
  filters?: DeliveryReportFilters,
): Promise<PaginatedResult<Delivery>> {
  if (pageUrl) {
    const allowedOrigin = new URL(import.meta.env.VITE_API_BASE_URL || window.location.origin, window.location.origin).origin
    const requestedUrl = new URL(pageUrl, allowedOrigin)
    if (requestedUrl.origin !== allowedOrigin) {
      throw new Error('A API retornou um endereço de paginação inválido.')
    }
  }
  const { data } = await api.get<PaginatedResult<Delivery>>(pageUrl ?? endpoints.account.deliveryReport(), {
    params: pageUrl ? undefined : Object.fromEntries(
      Object.entries(filters ?? {}).filter(([, value]) => value !== undefined && value !== ''),
    ),
  })
  if (!data || typeof data !== 'object' || !Array.isArray(data.results) || typeof data.count !== 'number') {
    throw new Error('A resposta da API do relatório de entregas não corresponde ao formato esperado.')
  }
  return { ...data, results: (data.results ?? []).map(normalizeDelivery) }
}

export async function fetchOperationsDashboard(params: {
  account_id?: string
  company_id?: string
  period_start?: string
  period_end?: string
}, pageUrl?: string): Promise<OperationsDashboard> {
  if (pageUrl) {
    const allowedOrigin = new URL(import.meta.env.VITE_API_BASE_URL || window.location.origin, window.location.origin).origin
    if (new URL(pageUrl, allowedOrigin).origin !== allowedOrigin) throw new Error('A API retornou um endereço de paginação inválido.')
  }
  const { data } = await api.get<OperationsDashboard>(pageUrl ?? endpoints.account.operationsDashboard(), {
    params: pageUrl ? undefined : Object.fromEntries(Object.entries(params).filter(([, value]) => Boolean(value))),
  })
  return { ...data, results: (data.results ?? []).map(normalizeDelivery) }
}

export async function fetchDeliverySummary(params: {
  account_id?: string
  company_id?: string
  period_start?: string
  period_end?: string
  created_at__gte?: string
  created_at__lte?: string
}): Promise<DeliverySummary> {
  const { data } = await api.get<DeliverySummary>(endpoints.account.deliverySummary(), {
    params: Object.fromEntries(Object.entries(params).filter(([, value]) => Boolean(value))),
  })
  return data
}

export async function fetchAccountSettings(accountId: string): Promise<AccountSettings> {
  const { data } = await api.get<AccountSettings>(endpoints.account.settings(), {
    params: { account_id: accountId },
  })
  return data
}

export async function saveAccountSettings(accountId: string, payload: AccountSettingsPayload): Promise<AccountSettings> {
  const { data } = await api.patch<AccountSettings>(endpoints.account.settings(), payload, {
    params: { account_id: accountId },
  })
  return data
}

export async function testCompanySefaz(companyId: string): Promise<SefazTestResult> {
  const { data } = await api.post<SefazTestResult>(endpoints.company.testSefaz(companyId))
  return data
}

export async function reassignDelivery(deliveryId: string, courierId: number): Promise<Delivery> {
  const { data } = await api.post<Delivery>(endpoints.delivery.reassign(deliveryId), { assigned_to: courierId })
  return normalizeDelivery(data)
}

export async function assignDeliveries(payload: { ids: string[]; assigned_to: number; planned_delivery_at: string | null }): Promise<{ results: DeliveryAssignmentResult[] }> {
  const { data } = await api.post<{ results: DeliveryAssignmentResult[] }>(endpoints.delivery.assign(), payload)
  return data
}

export async function generateDeliveriesFromInvoices(payload: { company_id: string; invoice_ids: string[]; assigned_to?: number | null; planned_delivery_at?: string | null; observations?: string; delivery_address?: DeliveryAddress | null; priority?: 'normal' | 'urgent' }): Promise<{ results: GenerateDeliveriesResult[] }> {
  const { data } = await api.post<{ results: GenerateDeliveriesResult[] }>(endpoints.delivery.fromInvoices(), payload)
  return data
}

export async function fetchDeliveryProofs(deliveryId: string): Promise<PaginatedResult<ProofOfDelivery>> {
  const { data } = await api.get<ProofOfDelivery[] | PaginatedResult<ProofOfDelivery>>(endpoints.delivery.proofs(deliveryId))
  return Array.isArray(data) ? { count: data.length, next: null, previous: null, results: data } : data
}

export async function fetchDeliveryTimeline(deliveryId: string): Promise<PaginatedResult<DeliveryTimelineEvent>> {
  const { data } = await api.get<DeliveryTimelineEvent[] | PaginatedResult<DeliveryTimelineEvent>>(endpoints.delivery.timeline(deliveryId))
  return Array.isArray(data) ? { count: data.length, next: null, previous: null, results: data } : data
}

export async function fetchDeliveryById(id: string): Promise<Delivery> {
  const { data } = await api.get<Delivery>(endpoints.delivery.detail(id))
  return normalizeDelivery(data)
}

export async function presignUploads(deliveryId: string, files: string[]): Promise<PresignResponse> {
  const { data } = await api.post<PresignResponse>(endpoints.delivery.presign(deliveryId), { files })
  return data
}

export async function createPod(deliveryId: string, payload: CreatePodPayload): Promise<ProofOfDelivery> {
  const body: CreatePodPayload = { ...payload, delivery: deliveryId }
  const { data } = await api.post<ProofOfDelivery>(endpoints.delivery.pods(), body)
  return data
}

// New: multipart POD upload (signature + multiple photos)
export async function uploadPodMultipart(deliveryId: string, form: FormData): Promise<ProofOfDelivery> {
  // Ensure delivery id is present in form as required by new endpoint
  if (!form.has('delivery')) form.append('delivery', deliveryId)
  const { data } = await api.post<ProofOfDelivery>(endpoints.delivery.pods(), form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function uploadToPresigned(upload: PresignUpload, file: File | Blob) {
  const form = new FormData()
  // Append required fields first
  Object.entries(upload.fields).forEach(([k, v]) => form.append(k, v))
  // The file field name for S3 should be 'file'
  form.append('file', file)
  const resp = await fetch(upload.url, { method: 'POST', body: form })
  if (!resp.ok) {
    throw new Error(i18n.t('errors.upload_failed'))
  }
  // Construct final URL. For S3, object is usually at `${url}/${fields.key}`
  const key = upload.fields['key']
  const finalUrl = key ? `${upload.url.replace(/\/$/, '')}/${key}` : upload.url
  return finalUrl
}

// User preferences update
export type UpdateUserPreferencesPayload = {
  cellphone?: string | null
  allow_delivery_notifications: boolean
  allow_email_notifications: boolean
  allow_push_notifications: boolean
  allow_sms_notifications: boolean
  allow_whatsapp_notifications: boolean
  preferred_language: string
}

export async function updateUserPreferences(userId: number | string, payload: UpdateUserPreferencesPayload): Promise<User> {
  const { data } = await api.patch<User>(endpoints.user.preferences(userId), payload)
  return data
}

export async function fetchNotifications(pageUrl?: string): Promise<NotificationPage> {
  const { data } = await api.get<NotificationPage>(pageUrl ?? endpoints.user.notifications(), {
    params: pageUrl ? undefined : { limit: 10, offset: 0 },
  })
  return data
}

export async function fetchUnreadNotificationCount(): Promise<number> {
  const { data } = await api.get<NotificationPage>(endpoints.user.notifications(), {
    params: { limit: 1, offset: 0, is_read: false },
  })
  return data.count
}

// Change password
export type ChangePasswordPayload = {
  current_password: string
  new_password: string
}

export async function changePassword(payload: ChangePasswordPayload): Promise<unknown> {
  const { data } = await api.post<unknown>(endpoints.user.changePassword(), payload)
  return data
}

export interface ContractQuote {
  quote_token: string
  quantity?: number
  retention_months?: number
  action: 'upgrade' | 'downgrade' | 'addon'
  amount_cents: number
  credit_cents: number
  recurring_cents: number
  setup_cents: number
  currency: string
  effective_at: string
  period_end: string
}

export async function quoteContract(accountId: string, selection: { plan_code?: string; addon_code?: string; quantity?: number }): Promise<ContractQuote> {
  const { data } = await api.post<ContractQuote>('/v1/financial/me/account/billing/contract-quote/', { account_id: accountId, ...selection })
  return data
}

export async function requestContractChange(accountId: string, quote: ContractQuote, key: string) {
  const { data } = await api.post<{ order_id: string; status: string; checkout_url: string; detail: string; plan_name: string }>('/v1/financial/me/account/billing/contract-change/', {
    account_id: accountId, quote_token: quote.quote_token, idempotency_key: key, consent: true,
  })
  return data
}

export interface ContractService {
  id: string; name: string; quantity: number; monthly_cents: number; status: string; order_id: string
}

export async function fetchContractServices(accountId: string): Promise<ContractService[]> {
  const { data } = await api.get<ContractService[]>('/v1/financial/me/account/billing/services/', { params: { account_id: accountId } })
  return data
}
